import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Loader, Center } from '@/components/ui';
import { EventsGate } from './components';

// Eager load dashboard for instant access
import EventsDashboard from './dashboard';

// Lazy load all other pages
const EventsListPage = lazy(() => import('./events'));
const EventFormPage = lazy(() => import('./events/form'));
const EventDetailPage = lazy(() => import('./events/detail'));
const ReviewPage = lazy(() => import('./review'));
const SourcesPage = lazy(() => import('./sources'));
const MappingsPage = lazy(() => import('./mappings'));
const TypesPage = lazy(() => import('./types'));
const RulesPage = lazy(() => import('./rules'));
const RuleEditorPage = lazy(() => import('./rules/editor'));
const LearningPage = lazy(() => import('./learning'));
const TagsPage = lazy(() => import('./tags'));
const PlaybooksPage = lazy(() => import('./playbooks'));
const PlaybookEditorPage = lazy(() => import('./playbooks/editor'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

/**
 * Event Management Routes
 *
 * Module access (Admin / EventsAdmin / "Events Access" / "Events Coordinator", or the Event.View right) is the domain
 * gate; inside, pages and actions gate on capabilities (role OR Event.* right) via EventsGate and
 * useEventsPermissions.
 */
export default function EventManagementRoutes() {
  return (
    <MainLayout domainKey="events">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Dashboard - Default route */}
          <Route path="/" element={<EventsGate allow={(p) => p.canView}><EventsDashboard /></EventsGate>} />

          {/* Events */}
          <Route path="/events" element={<EventsGate allow={(p) => p.canView}><EventsListPage /></EventsGate>} />
          <Route path="/events/new" element={<EventsGate allow={(p) => p.canCreate}><EventFormPage /></EventsGate>} />
          <Route path="/events/:id/edit" element={<EventsGate allow={(p) => p.canUpdate}><EventFormPage /></EventsGate>} />
          <Route path="/events/:id" element={<EventsGate allow={(p) => p.canView}><EventDetailPage /></EventsGate>} />
          <Route path="/review" element={<EventsGate allow={(p) => p.canReview}><ReviewPage /></EventsGate>} />

          {/* Planning Center */}
          <Route path="/sources" element={<EventsGate allow={(p) => p.canViewSources}><SourcesPage /></EventsGate>} />
          <Route path="/mappings" element={<EventsGate allow={(p) => p.canViewSources}><MappingsPage /></EventsGate>} />

          {/* Automation (Stage 1B) */}
          <Route path="/rules" element={<EventsGate allow={(p) => p.canManageRules}><RulesPage /></EventsGate>} />
          <Route path="/rules/new" element={<EventsGate allow={(p) => p.canManageRules}><RuleEditorPage /></EventsGate>} />
          <Route path="/rules/:ruleId" element={<EventsGate allow={(p) => p.canManageRules}><RuleEditorPage /></EventsGate>} />
          <Route path="/learning" element={<EventsGate allow={(p) => p.canReviewLearning}><LearningPage /></EventsGate>} />
          <Route path="/tags" element={<EventsGate allow={(p) => p.canManageTags}><TagsPage /></EventsGate>} />

          {/* Playbooks (Stage 2) */}
          <Route path="/playbooks" element={<EventsGate allow={(p) => p.canManagePlaybooks}><PlaybooksPage /></EventsGate>} />
          <Route path="/playbooks/new" element={<EventsGate allow={(p) => p.canManagePlaybooks}><PlaybookEditorPage /></EventsGate>} />
          <Route path="/playbooks/:playbookId" element={<EventsGate allow={(p) => p.canManagePlaybooks}><PlaybookEditorPage /></EventsGate>} />

          {/* Settings */}
          <Route path="/types" element={<EventsGate allow={(p) => p.canViewTypes}><TypesPage /></EventsGate>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app/events" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
